import { useState } from 'react';
import { Settings, Users, DollarSign, UserPlus, Edit, Trash2, Save, Mail, Link2 } from 'lucide-react';
import BillingDashboard from '@/components/dashboard/BillingDashboard';
import BrokerSettings from '@/components/settings/BrokerSettings';
import { api } from '@/api';
import { useAuth } from '@/context';

export default function SettingsHub() {
  const { user, logout, changePassword } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('account');
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // User management state (admin only)
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'user' });

  const tabs = [
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'broker', label: 'Broker Connection', icon: Link2 },
    ...(isAdmin ? [
      { id: 'users', label: 'User Management', icon: Users },
      { id: 'billing', label: 'Billing & Costs', icon: DollarSign },
    ] : []),
  ];

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      const response = await api.admin.getUsers();
      if (response.success) {
        setUsers(response.users || response.data || []);
      } else {
        setUsersError('Failed to load users');
      }
    } catch (err: any) {
      setUsersError('Error loading users: ' + err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const response = await api.admin.createUser(newUser);
      if (response.success) {
        setNewUser({ email: '', name: '', role: 'user' });
        setShowCreateForm(false);
        loadUsers();
      } else {
        alert('Failed to create user: ' + (response as any).error);
      }
    } catch (err: any) {
      alert('Error creating user: ' + err.message);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      const response = await api.admin.deleteUser(userId);
      if (response.success) loadUsers();
    } catch (err: any) {
      alert('Error deleting user: ' + err.message);
    }
  };

  const handleUpdateUser = async (userId: string, updates: any) => {
    try {
      const response = await api.admin.updateUser(userId, updates);
      if (response.success) {
        setEditingUser(null);
        loadUsers();
      }
    } catch (err: any) {
      alert('Error updating user: ' + err.message);
    }
  };

  // Load users when switching to users tab
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'users' && users.length === 0) {
      loadUsers();
    }
  };

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#0a0e27' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{
          width: '48px', height: '48px', borderRadius: '12px',
          background: '#3b82f622', border: '1px solid #3b82f644',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Settings size={24} style={{ color: '#3b82f6' }} />
        </div>
        <div>
          <h1 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: 700 }}>Settings</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>Account settings{isAdmin ? ', user management, and billing' : ''}</p>
        </div>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'flex', gap: '4px', padding: '4px',
        background: '#0f172a', borderRadius: '10px', border: '1px solid #1e293b',
        marginBottom: '24px', flexWrap: 'wrap'
      }}>
        {tabs.map(tab => {
          const TabIcon = tab.icon;
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 16px', borderRadius: '8px', border: 'none',
                background: isActive ? '#3b82f6' : 'transparent',
                color: isActive ? 'white' : '#94a3b8',
                fontSize: '13px', fontWeight: isActive ? 600 : 500,
                cursor: 'pointer', transition: 'all 0.2s',
              }}
            >
              <TabIcon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'account' && (
        <div style={{ maxWidth: '900px' }}>
          {/* Profile Information */}
          <div style={{ background: '#1e293b80', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ color: '#10b981', fontWeight: 600, marginBottom: '16px', marginTop: 0 }}>Profile Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Full Name</span>
                <span style={{ fontWeight: 500 }}>{user?.name || user?.email?.split('@')[0] || '-'}</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Email Address</span>
                <span style={{ fontWeight: 500 }}>{user?.email || '-'}</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Account Role</span>
                <span style={{
                  padding: '2px 10px', borderRadius: '8px', fontSize: '13px',
                  background: user?.role === 'admin' ? '#7c3aed33' : '#3b82f633',
                  color: user?.role === 'admin' ? '#a78bfa' : '#60a5fa',
                  textTransform: 'capitalize'
                }}>{user?.role || '-'}</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>User ID</span>
                <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{user?.id || '-'}</span>
              </div>
            </div>
          </div>

          {/* Platform Information */}
          <div style={{ background: '#1e293b80', border: '1px solid #334155', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ color: '#10b981', fontWeight: 600, marginBottom: '16px', marginTop: 0 }}>Platform Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Platform Version</span>
                <span style={{ fontWeight: 500 }}>v5.4.0</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>API Status</span>
                <span style={{ padding: '2px 10px', borderRadius: '8px', fontSize: '13px', background: '#10b98133', color: '#34d399' }}>Connected</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Data Source</span>
                <span style={{ fontWeight: 500 }}>TwelveData + Kraken</span>
              </div>
              <div style={{ color: 'white' }}>
                <span style={{ color: '#94a3b8', fontSize: '13px', display: 'block', marginBottom: '4px' }}>Environment</span>
                <span style={{ fontWeight: 500 }}>production</span>
              </div>
            </div>
          </div>

          {/* Security */}
          <div style={{ background: '#1e293b80', border: '1px solid #334155', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ color: '#10b981', fontWeight: 600, marginBottom: '16px', marginTop: 0 }}>Security</h3>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowPasswordChange(true)}
                style={{ padding: '10px 20px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
              >
                Change Password
              </button>
              <button
                onClick={() => logout()}
                style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && isAdmin && (
        <div>
          {/* Create User Button */}
          <div style={{ marginBottom: '20px' }}>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              style={{
                background: '#10b981', color: 'white', border: 'none', borderRadius: '8px',
                padding: '12px 24px', fontSize: '14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}
            >
              <UserPlus size={18} />
              Create New User
            </button>
          </div>

          {/* Create User Form */}
          {showCreateForm && (
            <div style={{ background: '#1e293b80', borderRadius: '12px', padding: '24px', border: '1px solid #334155', marginBottom: '20px' }}>
              <h3 style={{ color: '#10b981', marginBottom: '20px', marginTop: 0 }}>Create New User</h3>
              <div style={{ display: 'grid', gap: '16px', maxWidth: '500px' }}>
                <div>
                  <label style={{ color: '#94a3b8', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Email</label>
                  <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '14px' }}
                    placeholder="user@example.com" />
                </div>
                <div>
                  <label style={{ color: '#94a3b8', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Name</label>
                  <input type="text" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '14px' }}
                    placeholder="John Doe" />
                </div>
                <div>
                  <label style={{ color: '#94a3b8', display: 'block', marginBottom: '6px', fontSize: '13px' }}>Role</label>
                  <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    style={{ width: '100%', padding: '10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', color: 'white', fontSize: '14px' }}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button onClick={handleCreateUser} style={{ background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer' }}>Create</button>
                  <button onClick={() => setShowCreateForm(false)} style={{ background: '#64748b', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Loading/Error */}
          {usersLoading && <div style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>Loading users...</div>}
          {usersError && <div style={{ color: '#ef4444', textAlign: 'center', padding: '40px' }}>{usersError}</div>}

          {/* Users Table */}
          {!usersLoading && !usersError && users.length > 0 && (
            <div style={{ background: '#1e293b80', borderRadius: '12px', padding: '24px', border: '1px solid #334155' }}>
              <h3 style={{ color: '#10b981', marginBottom: '20px', marginTop: 0 }}>Users ({users.length})</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', color: 'white' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #334155' }}>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px' }}>Email</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px' }}>Name</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px' }}>Role</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px' }}>Status</th>
                      <th style={{ padding: '12px', textAlign: 'left', color: '#94a3b8', fontSize: '13px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u: any) => (
                      <tr key={u.user_id} style={{ borderBottom: '1px solid #334155' }}>
                        <td style={{ padding: '12px', fontSize: '13px' }}>{u.email}</td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>{u.name}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: u.role === 'admin' ? '#7c3aed' : '#3b82f6',
                            padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600
                          }}>{u.role}</span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: u.is_active ? '#10b981' : '#ef4444',
                            padding: '2px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 600
                          }}>{u.is_active ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td style={{ padding: '12px', display: 'flex', gap: '6px' }}>
                          <button onClick={() => setEditingUser(u.user_id)} style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}><Edit size={14} /></button>
                          <button onClick={() => handleDeleteUser(u.user_id)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'broker' && <BrokerSettings />}

      {activeTab === 'billing' && isAdmin && <BillingDashboard />}
    </div>
  );
}
